// @flow
import * as React from 'react';
import i18n from 'app/common/lib/i18n';
import PrettyNumber from 'app/common/react/i18n/PrettyNumber';
import type { MergeFlowBoard } from 'app/common/react/components/BoardEdit/BoardMergeModalContent';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { Box, Text } from 'gestalt';

type BoardMergePeopleLossWarningProps = {
  boardName: string,
  collaboratorCount: number,
};

function BoardDeletionWarning({ boardName, collaboratorCount }: BoardMergePeopleLossWarningProps) {
  const template = collaboratorCount
    ? i18n._(
        "When you delete {{ boardName }}, you'll lose {{ n_collaborators }} and all followers of that board.",
        'helper text for selecting merge target board',
      )
    : i18n._(
        "When you delete {{ boardName }}, you'll lose all followers of that board.",
        'helper text for selecting merge target board',
      );
  return (
    <Text>
      {interpolateNamedTemplate(template, {
        boardName: (
          <Text bold inline key="board_name">
            {boardName}
          </Text>
        ),
        n_collaborators: collaboratorCount ? (
          <Text bold inline key="collaborator_count">
            {interpolateNamedTemplate(
              i18n.ngettext(
                '{{ count }} collaborator',
                '{{ count }} collaborators',
                collaboratorCount,
                'label for n board collaborator(s)',
              ),
              {
                count: <PrettyNumber value={collaboratorCount} />,
              },
            )}
          </Text>
        ) : null,
      })}
    </Text>
  );
}

function YourBoardText({ board: { name, is_collaborative } }: { board: MergeFlowBoard }) {
  const template = is_collaborative
    ? i18n._('your {{ boardName }} group board')
    : i18n._('your {{ boardName }} board');
  return (
    <Text inline key={`yourboard_${name}`}>
      {interpolateNamedTemplate(template, {
        boardName: (
          <Text bold inline key={`board_${name}`}>
            {name}
          </Text>
        ),
      })}
    </Text>
  );
}

type BoardMergeWarningProps = {
  sourceBoard: MergeFlowBoard,
  targetBoard: MergeFlowBoard,
};

export default function BoardMergeWarning({ sourceBoard, targetBoard }: BoardMergeWarningProps) {
  return (
    <Box>
      <Box paddingY={2}>
        <Text>
          {interpolateNamedTemplate(
            i18n._(
              "You're about to move all the Pins from {{ sourceBoardName }}" +
                ' to a new section in {{ targetBoardName }}.',
              'helper text for selecting merge target board',
            ),
            {
              sourceBoardName: <YourBoardText key="sourceBoard" board={sourceBoard} />,
              targetBoardName: <YourBoardText key="targetBoard" board={targetBoard} />,
            },
          )}
        </Text>
      </Box>
      <Box paddingY={2}>
        <BoardDeletionWarning
          boardName={sourceBoard.name}
          collaboratorCount={sourceBoard.collaborator_count}
        />
      </Box>
    </Box>
  );
}
